import { AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { useEffect, useRef, useState, UIEvent } from 'react';
import { Skeleton, useToastNotificationContext } from '@sendible/design-system/src';
import validators from '@sendible/common/utils/validators';
import { NotificationTypeEnum } from '@sendible/design-system/src/components/molecules/Toast';
import { useGetMediasForComposeBox } from '@sendible/frontendv2/src/models/medias';
import { MediaFileType } from '@sendible/frontendv2/src/models/medias/types';
import { Container, MediaSlot, MotionButton, Slider } from './index.styles';
import { ComposeMediaCard } from './components/ComposeMediaCard';
import { composeMediaInteractions } from '../../composeMediaInteractions';
import { useComposeBoxContext } from '../../composeBoxContext';
import {
  EDITOR_CLOSE_VIDEO,
  EDITOR_CLOSE_WITH_ERROR,
  EDITOR_CLOSE,
  EDITOR_EXPORT,
  EDITOR_OPEN,
  EDITOR_VIDEO_EXPORT,
  EDITOR_VIDEO_OPEN,
} from '../../../CreativeEditor';

export const MediaAttached = () => {
  const ref = useRef<HTMLDivElement>(null);
  const attachedMediasOnActiveTab = useGetMediasForComposeBox();
  const [showPreviousButton, setShowPreviousButton] = useState(false);
  const [showNextButton, setShowNextButton] = useState(!!(attachedMediasOnActiveTab && attachedMediasOnActiveTab?.length > 6));
  const { activeComposeBoxTab, activeTabService } = useComposeBoxContext();
  const { showToastNotification } = useToastNotificationContext();

  const iframeElement: HTMLIFrameElement | null = document.getElementById('image-editor-iframe') as HTMLIFrameElement;

  const handlePrevSliderButton = () => {
    if (ref.current) {
      const { scrollLeft, children } = ref.current;
      const mediaWidth = children[0].scrollWidth;

      ref.current.scrollLeft = scrollLeft - mediaWidth;
    }
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (validators.validatePostMessageSource(event) && iframeElement) {
        if (event.data.event === EDITOR_CLOSE_VIDEO || event.data.event === EDITOR_CLOSE) {
          iframeElement.style.display = 'none';
        }

        if (event.data.event === EDITOR_VIDEO_EXPORT || event.data.event === EDITOR_EXPORT) {
          iframeElement.style.display = 'none';
          composeMediaInteractions.replaceMedia(event.data.originalMediaId.toString(), event.data.mediaId, activeComposeBoxTab);
        }

        if (event.data.event === EDITOR_CLOSE_WITH_ERROR) {
          iframeElement.style.display = 'none';

          showToastNotification({
            hideTimeout: 10,
            notificationText: event.data.message,
            type: NotificationTypeEnum.Error,
            showCloseButton: true,
          });
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleNextSliderButton = () => {
    if (ref.current) {
      const { scrollLeft, children } = ref.current;
      const mediaWidth = children[0].scrollWidth;

      ref.current.scrollLeft = scrollLeft + mediaWidth;
    }
  };

  const handleOpenEditor = (mediaID: MediaIdType) => {
    const mediaObject = attachedMediasOnActiveTab ? attachedMediasOnActiveTab.find((media) => media.id === mediaID) : null;

    if (mediaObject && iframeElement && iframeElement.contentWindow) {
      if (mediaObject.type === MediaFileType.Video) {
        iframeElement.style.display = 'block';
        iframeElement.contentWindow.postMessage(
          {
            eventName: EDITOR_VIDEO_OPEN,
            data: {
              originalUrl: mediaObject.originalUrl,
              sourceSets: mediaObject.sourceSets,
              originalMediaId: mediaObject.id,
              fileName: mediaObject.name,
              serviceSet: activeTabService,
            },
          },
          '*'
        );
      }

      if (mediaObject.type === MediaFileType.Image) {
        iframeElement.style.display = 'block';
        iframeElement.contentWindow.postMessage({ eventName: 'preload-editor' }, '*');
        iframeElement.contentWindow.postMessage(
          {
            eventName: EDITOR_OPEN,
            data: {
              pendoUserData: window.pendoUserData,
              pendoAccountData: window.pendoAccountData,
              loginContext: window.loginContext.getLoginContextForAPI(),
              domainUrl: window.location.origin,
              imageUrl: mediaObject.originalUrl,
              serviceSet: activeTabService,
              restrictedServices: {
                pinterest: !window.js_featureTagsManager?.isFeatureTag('enablePinterest'),
                threads: !window.js_featureTagsManager?.isFeatureTag('threads'),
                bluesky: !window.js_featureTagsManager?.isFeatureTag('bluesky'),
              },
              originalMediaId: mediaObject.id,
              name: mediaObject.name,
              isNewMediaSystem: true,
            },
          },
          '*'
        );
      }
    }
  };

  const handleDropdownClick = (mediaID: MediaIdType, option: string) => {
    if (option === 'remove') {
      composeMediaInteractions.removeMedia(mediaID, activeComposeBoxTab);
    }

    if (option === 'edit') {
      handleOpenEditor(mediaID);
    }
  };

  const onSliderScroll = (e: UIEvent) => {
    const { offsetWidth, scrollLeft, scrollWidth } = e.target as HTMLElement;
    const limit = offsetWidth + scrollLeft;

    if (scrollLeft > 0) setShowPreviousButton(true);
    else setShowPreviousButton(false);

    if (scrollWidth > offsetWidth && limit !== scrollWidth) setShowNextButton(true);
    else setShowNextButton(false);
  };

  useEffect(() => {
    if (attachedMediasOnActiveTab && attachedMediasOnActiveTab?.length > 5) setShowNextButton(true);
    else setShowNextButton(false);
  }, [attachedMediasOnActiveTab?.length]);

  return createPortal(
    <Container>
      <>
        {ref.current && (
          <AnimatePresence>
            {showPreviousButton ? (
              <MotionButton
                className="attach-prev nav-button"
                icon="arrow_left"
                key="prev-button"
                onClick={handlePrevSliderButton}
                size={16}
                initial={{ x: '-5rem' }}
                animate={{ x: 0 }}
                exit={{ x: '-5rem' }}
              />
            ) : null}
            {showNextButton ? (
              <MotionButton
                className="attach-next nav-button"
                icon="arrow_right"
                key="next-button"
                onClick={handleNextSliderButton}
                size={16}
                initial={{ x: '5rem' }}
                animate={{ x: 0 }}
                exit={{ x: '5rem' }}
              />
            ) : null}
          </AnimatePresence>
        )}
        <Slider
          onScroll={onSliderScroll}
          ref={ref}
        >
          {attachedMediasOnActiveTab?.map((media, index) => (
            <MediaSlot
              key={`${media.id}-${index}`}
              className="media-slot"
            >
              {media.status != 'Ready' ? (
                <Skeleton
                  key={`loading_${index}`}
                  shape="square"
                  width="6.5rem"
                />
              ) : (
                <ComposeMediaCard
                  ariaMediaCardLabel="compose-media-label"
                  dropdownButtonClick={(option: string) => {
                    handleDropdownClick(media.id, option);
                  }}
                  id={media.id}
                  isGif={media.metadata && media.metadata?.mediaType?.includes('gif')}
                  mediaType={media.type}
                  open={(id: MediaIdType) => null}
                  orderNumber={attachedMediasOnActiveTab?.length > 1 ? `${Number(index) + 1}` : ''}
                  tabIndex={0}
                  testId="asset-video"
                  thumbUrl={media.thumbnailUrl}
                  validationError={media.validationError}
                  videoDuration={media.metadata?.duration}
                />
              )}
            </MediaSlot>
          ))}
        </Slider>
      </>
    </Container>,
    document.getElementById('mediaAttached') as HTMLElement
  );
};
